import { mapGetters, mapMutations } from 'vuex'
import { Inertia } from '@inertiajs/inertia'
import filled from '../util/filled'

export default {
  created() {
    this.removeOnNavigationChangesEvent = Inertia.on('before', event => {
      this.removeOnNavigationChangesEvent()
      this.handlePreventFormAbandonmentOnInertia(event)
    })

    window.addEventListener('beforeunload', this.handleBeforeUnload)
    document.addEventListener('keydown', this.handleReloadKeydown, this.reloadKeyListenerOptions)

    this.removeOnBeforeUnloadEvent = () => {
      window.removeEventListener('beforeunload', this.handleBeforeUnload)
      document.removeEventListener(
        'keydown',
        this.handleReloadKeydown,
        this.reloadKeyListenerOptions
      )

      this.removeOnBeforeUnloadEvent = () => {}
    }
  },

  mounted() {
    window.onpopstate = event => {
      this.handlePreventFormAbandonmentOnPopState(event)
    }
  },

  beforeUnmount() {
    this.removeOnBeforeUnloadEvent()
  },

  unmounted() {
    this.removeOnNavigationChangesEvent()
    this.resetPushState()
  },

  data() {
    return {
      removeOnNavigationChangesEvent: null,
      removeOnBeforeUnloadEvent: null,
      navigateBackUsingHistory: true,
      reloadKeyListenerOptions: { capture: true },
      reloadDialogOpen: false,
    }
  },

  methods: {
    ...mapMutations([
      'allowLeavingForm',
      'preventLeavingForm',
      'triggerPushState',
      'resetPushState',
    ]),

    /**
     * Prevent accidental abandonment only if form was changed.
     */
    updateFormStatus() {
      if (this.canLeaveForm === true) {
        this.triggerPushState()
      }

      this.preventLeavingForm()
    },

    enableNavigateBackUsingHistory() {
      this.navigateBackUsingHistory = false
    },

    disableNavigateBackUsingHistory() {
      this.navigateBackUsingHistory = false
    },

    /**
     * Show the custom abandonment dialog for in-app navigation.
     *
     * @param {Function} proceed
     * @param {Function} revert
     * @param {'leave'|'reload'} reason
     * @param {{ deferRevert?: boolean }} options
     * @returns {void}
     */
    handlePreventFormAbandonment(proceed, revert, reason = 'leave', options = {}) {
      if (this.canLeaveForm) {
        proceed()
        return
      }

      if (!options.deferRevert) {
        revert()
      }

      Nova.$emit('confirm-form-abandonment', {
        proceed,
        revert: options.deferRevert ? revert : () => {},
        reason,
      })
    },

    /**
     * Guard page unload with the browser-native prompt when needed.
     *
     * @param {BeforeUnloadEvent} event
     * @returns {void}
     */
    handleBeforeUnload(event) {
      if (this.canLeaveForm || this.reloadDialogOpen) {
        return
      }

      event.preventDefault()
      event.returnValue = ''
    },

    /**
     * Detect browser reload keyboard shortcuts.
     *
     * @param {KeyboardEvent} event
     * @returns {boolean}
     */
    isReloadShortcut(event) {
      if (event.key === 'F5' || event.code === 'F5') {
        return true
      }

      const isRKey =
        event.key.toLowerCase() === 'r' || event.code === 'KeyR'

      return isRKey && (event.ctrlKey || event.metaKey)
    },

    /**
     * Intercept keyboard reload shortcuts to show the custom dialog.
     *
     * @param {KeyboardEvent} event
     * @returns {void}
     */
    handleReloadKeydown(event) {
      if (!this.isReloadShortcut(event) || this.canLeaveForm) {
        return
      }

      event.preventDefault()
      event.stopImmediatePropagation()

      if (this.reloadDialogOpen) {
        return
      }

      this.reloadDialogOpen = true
      window.removeEventListener('beforeunload', this.handleBeforeUnload)

      this.handlePreventFormAbandonment(
        () => {
          this.reloadDialogOpen = false
          this.handleProceedingToNextPage()
          this.allowLeavingForm()
          window.location.reload()
        },
        () => {
          this.reloadDialogOpen = false
          window.addEventListener('beforeunload', this.handleBeforeUnload)
        },
        'reload',
        { deferRevert: true }
      )
    },

    handlePreventFormAbandonmentOnInertia(event) {
      const visit = event.detail.visit

      this.handlePreventFormAbandonment(
        () => {
          this.handleProceedingToNextPage()
          this.allowLeavingForm()

          Inertia.visit(visit.url, {
            method: visit.method,
            data: visit.data,
            replace: visit.replace,
            preserveScroll: visit.preserveScroll,
            preserveState: visit.preserveState,
            only: visit.only,
            headers: visit.headers,
            errorBag: visit.errorBag,
          })
        },
        () => {
          Inertia.ignoreHistoryState = true
          event.preventDefault()

          this.removeOnNavigationChangesEvent = Inertia.on('before', event => {
            this.removeOnNavigationChangesEvent()
            this.handlePreventFormAbandonmentOnInertia(event)
          })
        }
      )
    },

    handlePreventFormAbandonmentOnPopState(event) {
      event.stopImmediatePropagation()
      event.stopPropagation()

      this.handlePreventFormAbandonment(
        () => {
          this.handleProceedingToPreviousPage()
          this.allowLeavingForm()
        },
        () => {
          this.triggerPushState()
        }
      )
    },

    handleProceedingToPreviousPage() {
      window.onpopstate = null
      Inertia.ignoreHistoryState = false

      this.removeOnBeforeUnloadEvent()

      if (!this.canLeaveFormToPreviousPage && this.navigateBackUsingHistory) {
        window.history.back()
      }
    },

    handleProceedingToNextPage() {
      window.onpopstate = null
      Inertia.ignoreHistoryState = false

      this.removeOnBeforeUnloadEvent()
    },

    proceedToPreviousPage(url) {
      if (this.navigateBackUsingHistory && window.history.length > 1) {
        window.history.back()
      } else if (!this.navigateBackUsingHistory && filled(url)) {
        Nova.visit(url, { replace: true })
      } else {
        Nova.visit('/')
      }
    },
  },

  computed: {
    ...mapGetters(['canLeaveForm', 'canLeaveFormToPreviousPage']),
  },
}
